import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Settings, User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useLogout, useUser } from "@/features/auth/store";
import { NotificationBellDropdown } from "@/features/notifications/components/NotificationBellDropdown";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Routes, Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { getPageTitle } from "@/constants/admin-nav";
import { cn } from "@/lib/utils";

const SETTINGS_URL = appPath(Pages.SETTINGS);
const PROFILE_URL = appPath(Routes.PROFILE);

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

export function AppHeader() {
  const user = useUser();
  const logout = useLogout();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const pageTitle = getPageTitle(location.pathname, location.search);

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "حسابي";
  const roleName = user?.role ? getRoleDisplayName(user.role) : "";

  return (
    <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/90 px-4 md:px-6 backdrop-blur supports-backdrop-filter:bg-background/75">
      {isMobile && (
        <>
          <SidebarTrigger className="-mr-1" aria-label="فتح القائمة الجانبية" />
          <Separator orientation="vertical" className="mr-2 h-4" aria-hidden />
        </>
      )}

      <div className="flex min-w-0 flex-1 items-center ">
        <h1 className="truncate text-base font-semibold text-foreground md:text-lg">
          {pageTitle}
        </h1>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-1">
        <NotificationBellDropdown align="end" />

        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger
            className={cn(
              "relative flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-card p-0 shadow-none outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring",
            )}
            aria-label="قائمة الحساب"
          >
            <Avatar className="size-9">
              <AvatarFallback className="bg-primary text-xs font-medium text-primary-foreground">
                {initials(displayName)}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            side="bottom"
            className={cn(
              "min-w-56 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg",
            )}
          >
            <div className="flex flex-col px-3 py-2.5 text-right">
              <p className="truncate text-sm font-semibold leading-none text-foreground">
                {displayName}
              </p>
              {user?.email && (
                <p className="mt-1.5 truncate text-xs leading-none text-muted-foreground">
                  {user.email}
                </p>
              )}
            </div>

            {roleName && (
              <>
                <div className="my-1 h-px bg-border" />
                <div className="px-3 py-2 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                    الدور
                  </p>
                  <p className="mt-1 truncate text-xs font-medium text-foreground">
                    {roleName}
                  </p>
                </div>
              </>
            )}

            <div className="my-1 h-px bg-border" />

            <DropdownMenuItem className="p-0">
              <Link
                to={PROFILE_URL}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2"
              >
                <User className="size-4 shrink-0" aria-hidden />
                <span className="font-medium">الملف الشخصي</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem className="p-0">
              <Link
                to={SETTINGS_URL}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2"
              >
                <Settings className="size-4 shrink-0" aria-hidden />
                <span className="font-medium">الإعدادات</span>
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem
              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
            >
              <LogOut className="size-4 shrink-0" aria-hidden />
              <span className="font-medium">تسجيل الخروج</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
